import React, { useEffect, useState } from 'react';
import { useAppDispatch } from 'flow/hooks/useRedux';
import { generatePath, useHistory, useParams } from 'react-router-dom';
import { fromJS, List } from 'immutable';
import { updateChannelMember } from 'flow/actions/channels-actions';
import { updateQueueMember } from 'flow/actions/pbx-actions';
import { updateProfiles } from 'flow/actions/profile-actions';
import { selectQueueMembers } from 'flow/selectors/queue-selectors';
import Divider from 'flow/components/ui-components/Divider';
import Toggle from 'flow/components/ui-components/Toggle';
import { useModal } from 'flow/contexts/ModalProvider';
import useAppContext from 'flow/hooks/useAppContext';
import useChannel from 'flow/hooks/useChannel';
import useExtension from 'flow/hooks/useExtension';
import useProfiles from 'flow/hooks/useProfiles';
import useSinglePaneMode from 'flow/hooks/useSinglePaneMode';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { ChannelLoginList, Profile, QueueLoginList } from 'flow/types/Profile';
import { AppDispatch } from 'flow/types/Redux';
import { routes } from 'flow/types/routes';
import { State } from 'flow/types/State';
import { TypedMap } from 'flow/types/TypedMap';
import { getQueueChannelDescription } from 'flow/utils/pbx-utils';
import { channelLoginVaries, queueLoginVaries } from 'flow/utils/profile-utils';
import Page from './Page';
import LiveChats from './QueueAndChatLogin/LiveChats';
import Queues from './QueueAndChatLogin/Queues';
import { LocationType } from 'flow/types/Location';

const QueueAndChatLogin = () => {
    const { i18next } = useAppContext();
    const dispatch = useAppDispatch();
    const history = useHistory<LocationType>();
    const { openModal } = useModal();

    const isAiCoworker = window.location.pathname.includes('ai-coworker');
    const { extensionKey } = useParams<{
        extensionKey: ExtensionKey;
    }>();

    const { channels } = useChannel(extensionKey);
    const { extension } = useExtension(extensionKey);
    const { profiles } = useProfiles(extensionKey);
    const { isSinglePaneMode } = useSinglePaneMode();

    const [perProfile, setPerProfile] = useState(false);
    const [varies, setVaries] = useState(null);

    useEffect(() => {
        if (!profiles) {
            return;
        }

        const profilesVary =
            queueLoginVaries(profiles) || channelLoginVaries(profiles);
        setPerProfile(profilesVary);
        setVaries(profilesVary);
    }, [profiles]);

    if (!profiles?.size) {
        return null;
    }

    const getTitle = () => {
        const listExistsOnProfile = (
            list:
                | List<TypedMap<ChannelLoginList>>
                | List<TypedMap<QueueLoginList>>,
        ) => !!list && !list.isEmpty();

        const hasChannel = profiles.some((profile) =>
            listExistsOnProfile(profile.get('channelLoginList')),
        );

        const hasQueue = profiles.some((profile) =>
            listExistsOnProfile(profile.get('queueLoginList')),
        );

        if ((hasChannel && hasQueue) || (!hasChannel && !hasQueue)) {
            return 'settings.queue_channel_login';
        }

        return hasChannel ? 'settings.channel_login' : 'settings.queue_login';
    };

    const toggleProfileHandler = (value: boolean) => {
        if (perProfile && varies) {
            openModal('confirmationDialog', {
                title: i18next.t('settings.reset.warning.general.Title'),
                description: (
                    <div
                        dangerouslySetInnerHTML={{
                            __html: i18next.t(
                                'settings.reset.warning.general',
                                {
                                    currentSettings: getQueueChannelDescription(
                                        profiles,
                                        i18next,
                                    ),
                                    firstProfileName: profiles
                                        .first()
                                        .get('description'),
                                },
                            ),
                        }}
                    />
                ),
                handleAccept: handleConfirm,
            });
        } else {
            setPerProfile(value);
        }
    };

    const footer = (
        <>
            <p>{i18next.t('settings.profile_edit')}</p>
            <Toggle
                value={perProfile}
                onChange={toggleProfileHandler}
                text={i18next.t('settings.profile_edit')}
            />
        </>
    );

    const updateChannelMemberLoggedIn = (
        channelLogin: TypedMap<ChannelLoginList>,
    ) => {
        const channelKey = channelLogin.get('channelEntityKey');
        const newValue = channelLogin.get('loggedIn');
        const updatedMemberDTO = channels
            .getIn([channelKey, 'channelMemberDTOs'])
            .find(
                (member) =>
                    member.get('chatUserKey') === extension.get('chatUserKey'),
            )
            .set('loggedIn', newValue);
        dispatch(
            updateChannelMember(
                channelKey,
                updatedMemberDTO.get('key'),
                updatedMemberDTO,
            ),
        );
    };

    const updateAllQueuesAndChannelsToFirst = () => {
        let updatedProfileList = profiles;
        const firstQueueLoginList = profiles.first().get('queueLoginList');
        const firstChannelLoginList = profiles.first().get('channelLoginList');

        firstChannelLoginList.forEach((channelLogin) => {
            updatedProfileList = updateProfileListFromChannel(
                updatedProfileList,
                channelLogin,
            );
        });
        firstQueueLoginList.forEach((queueLogin) => {
            updatedProfileList = updateProfileListFromQueue(
                updatedProfileList,
                queueLogin,
            );
        });

        dispatch(updateProfiles(extensionKey, updatedProfileList));
        firstQueueLoginList.forEach((queueLogin) => {
            dispatch(updateQueueMemberLoggedIn(queueLogin, extensionKey));
        });
        firstChannelLoginList.forEach((channelLogin) => {
            updateChannelMemberLoggedIn(channelLogin);
        });
    };

    const updateProfileListFromChannel = (
        profileList: List<TypedMap<Profile>>,
        channelLogin: TypedMap<ChannelLoginList>,
    ) => {
        const channelKey = channelLogin.get('channelEntityKey');
        const newValue = channelLogin.get('loggedIn');

        return profileList.map((profile) =>
            profile.update(
                'channelLoginList',
                List<TypedMap<ChannelLoginList>>(),
                (prevChannelLogins: List<TypedMap<ChannelLoginList>>) => {
                    const indexOfChannelToUpdate = prevChannelLogins.findIndex(
                        (login) => login.get('channelEntityKey') === channelKey,
                    );

                    if (indexOfChannelToUpdate === -1) {
                        return prevChannelLogins.push(
                            fromJS({
                                channelEntityKey: channelKey,
                                loggedIn: newValue,
                            }),
                        );
                    }
                    return prevChannelLogins.setIn(
                        [indexOfChannelToUpdate, 'loggedIn'],
                        newValue,
                    );
                },
            ),
        );
    };

    const updateProfileListFromQueue = (
        profileList: List<TypedMap<Profile>>,
        queues: TypedMap<QueueLoginList>,
    ) => {
        const queueKey = queues.get('queueEntityKey');
        const value = queues.get('loggedIn');
        return profileList.map((profile) =>
            profile.update(
                'queueLoginList',
                List<TypedMap<QueueLoginList>>(),
                (prevQueueLogins: List<TypedMap<QueueLoginList>>) => {
                    const indexOfQueueToUpdate = prevQueueLogins.findIndex(
                        (login) => login.get('queueEntityKey') === queueKey,
                    );

                    if (indexOfQueueToUpdate === -1) {
                        return prevQueueLogins.push(
                            fromJS({
                                channelEntityKey: queueKey,
                                loggedIn: value,
                            }),
                        );
                    }
                    return prevQueueLogins.setIn(
                        [indexOfQueueToUpdate, 'loggedIn'],
                        value,
                    );
                },
            ),
        );
    };

    const getInfo = () => {
        const isMemberOfChannel = profiles.find(
            (profile) => !profile.get('channelLoginList').isEmpty(),
        );
        const isMemberOfQueue = profiles.find(
            (profile) => !profile.get('queueLoginList').isEmpty(),
        );

        if (
            (isMemberOfChannel && isMemberOfQueue) ||
            (!isMemberOfChannel && !isMemberOfQueue)
        ) {
            return i18next.t('settings.queue_channel_login.info');
        }

        return isMemberOfChannel
            ? i18next.t('settings.channel_login.info')
            : i18next.t('settings.queue_login.info');
    };

    if (!profiles?.size) {
        return null;
    }
    const handleConfirm = () => {
        updateAllQueuesAndChannelsToFirst();
        setVaries(false);
        setPerProfile(false);
    };

    let onClickBack: (() => void) | null;
    if (history.location.state?.from) {
        onClickBack = () => history.push(history.location.state?.from);
    } else if (isSinglePaneMode) {
        onClickBack = () =>
            history.push(
                generatePath(routes['settings'], {
                    extensionKey,
                }),
            );
    } else {
        onClickBack = null;
    }

    return (
        <Page header={getTitle()} onClickBack={onClickBack} footer={footer}>
            <p
                className="ps-2 pt-4 pb-6"
                dangerouslySetInnerHTML={{
                    __html: getInfo(),
                }}
            />
            <Queues perProfile={perProfile} />
            {!isAiCoworker && (
                <>
                    <Divider />
                    <LiveChats perProfile={perProfile} />
                </>
            )}
        </Page>
    );
};

export default QueueAndChatLogin;

const updateQueueMemberLoggedIn =
    (queue: TypedMap<QueueLoginList>, extensionKey: ExtensionKey) =>
    (dispatch: AppDispatch, getState: () => State) => {
        const queueEntityKey = queue.get('queueEntityKey');
        const queueMembers = selectQueueMembers(getState(), queueEntityKey);
        const memberDTO = queueMembers?.find(
            (member) => member.get('extensionKey') === extensionKey,
        );

        if (!memberDTO) {
            console.warn('No queue member found to update loggedIn status'); // Should never happen
            return;
        }

        const updatedMemberDTO = memberDTO.set(
            'loggedIn',
            queue.get('loggedIn'),
        );
        dispatch(updateQueueMember(queueEntityKey, updatedMemberDTO));
    };
